import React, { useState, useEffect } from "react"
import { Row, Col, Card, CardBody, Alert, Spinner, Badge } from "reactstrap"
import { get, post } from "helpers/api_helper"
import {
    TRACKING_INTEGRATION_URL,
    TRACKING_TOGGLE_URL,
    TRACKING_SYNC_AGENTS_URL,
    TRACKING_SYNC_CLIENTS_URL,
    SCANNING_ACCESS_URL
} from "helpers/url_helper"
import Notifications from "components/Notifications"
import ScanningBookingsModal from "./scanning_bookings_modal"
import moment from "moment"

const ScanningIntegration = () => {
    const [integrationStatus, setIntegrationStatus] = useState(null)
    const [isIntegrated, setIsIntegrated] = useState(false)
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(false)
    const [launching, setLaunching] = useState(false)
    const [agentSyncLoading, setAgentSyncLoading] = useState(false)
    const [clientSyncLoading, setClientSyncLoading] = useState(false)
    const [isBookingsModalOpen, setIsBookingsModalOpen] = useState(false)
    const [notice, setNotice] = useState(null)

    // Sub-modules state
    const [agentSyncActive, setAgentSyncActive] = useState(true)
    const [clientSyncActive, setClientSyncActive] = useState(true)
    const [trackingModuleActive, setTrackingModuleActive] = useState(true)

    const getOrgId = () => {
        try {
            const auth = JSON.parse(localStorage.getItem("authUser")) || {}
            return auth.org_id || auth.org || auth.id || 24001
        } catch (e) {
            return 24001
        }
    }

    const fetchIntegrationStatus = async () => {
        setLoading(true)
        try {
            const org = getOrgId()
            const res = await get(`${TRACKING_INTEGRATION_URL}?org=${org}`)
            if (res && res.status === 200 && res.response) {
                setIsIntegrated(Boolean(res.response.is_integrated))
                setIntegrationStatus(res.response.data)
                if (res.response.data?.is_tracking !== undefined) {
                    setTrackingModuleActive(Boolean(res.response.data.is_tracking))
                }
            }
        } catch (e) {
            console.error("Failed to load tracking integration status", e)
        } finally {
            setLoading(false)
        }
    }

    // Toggle Tracking Module on OralStop Cloud
    const handleToggleTracking = async (enabled) => {
        setTrackingModuleActive(enabled)
        try {
            const org = getOrgId()
            const token = localStorage.getItem("token")
            const res = await post(`${TRACKING_TOGGLE_URL}?org=${org}`, {
                org: org,
                is_tracking: enabled ? 1 : 0,
                auth_token: token
            })
            if (res && res.status === 200) {
                Notifications(enabled ? "Tracking Module Enabled on cloud." : "Tracking Module Disabled on cloud.", "success")
                fetchIntegrationStatus()
            } else {
                Notifications(res?.message || "Failed to update tracking module status.", "error")
            }
        } catch (e) {
            Notifications("Error updating tracking module status.", "error")
        }
    }

    useEffect(() => {
        fetchIntegrationStatus()
    }, [])

    // Master Toggle: Activate Scanning & Tracking Integration
    const handleMasterToggle = async (enable) => {
        if (!enable) {
            setIsIntegrated(false)
            Notifications("Scanning & Tracking integration disabled.", "info")
            return
        }

        setActionLoading(true)
        setNotice(null)
        try {
            const org = getOrgId()
            const res = await post(`${TRACKING_INTEGRATION_URL}?org=${org}`, {
                org: org,
                lab_code: "dglab"
            })
            if (res && res.status === 200 && res.response) {
                setIsIntegrated(true)
                setIntegrationStatus(res.response.data)
                Notifications("Scanning & Tracking activated! Lab registered & background sync started.", "success")
                setNotice({
                    type: "success",
                    text: "OralStop Scanning & Tracking activated successfully. Lab ID verified and token cached."
                })
            } else {
                Notifications(res?.message || "Failed to activate integration.", "error")
            }
        } catch (e) {
            Notifications("Error activating scanning integration.", "error")
        } finally {
            setActionLoading(false)
        }
    }

    // 1-Click Launch & Auto-Login
    const handleLaunchPortal = async () => {
        setLaunching(true)
        try {
            const org = getOrgId()
            const res = await get(`${SCANNING_ACCESS_URL}?org=${org}`)
            if (res && res.status === 200 && res.response?.accessurl) {
                window.open(res.response.accessurl, "_blank")
            } else {
                const accessUrl = integrationStatus?.response?.data?.accessUrl ||
                    integrationStatus?.response?.accessUrl ||
                    "https://scan.oralstop.com/login";
                window.open(accessUrl, "_blank")
            }
        } catch (e) {
            window.open("https://scan.oralstop.com/login", "_blank")
        } finally {
            setLaunching(false)
        }
    }

    // Manual Agent Sync
    const handleSyncAgents = async () => {
        setAgentSyncLoading(true)
        try {
            const org = getOrgId()
            const res = await post(`${TRACKING_SYNC_AGENTS_URL}?org=${org}`, { org: org })
            if (res && res.status === 200) {
                Notifications(res.response || "Agent synchronization triggered successfully in background.", "success")
                fetchIntegrationStatus()
            } else {
                Notifications(res?.message || "Failed to sync agents.", "error")
            }
        } catch (e) {
            Notifications("Error syncing technicians/agents.", "error")
        } finally {
            setAgentSyncLoading(false)
        }
    }

    // Manual Client Sync
    const handleSyncClients = async () => {
        setClientSyncLoading(true)
        try {
            const org = getOrgId()
            const res = await post(`${TRACKING_SYNC_CLIENTS_URL}?org=${org}`, { org: org })
            if (res && res.status === 200) {
                Notifications(res.response || "Client synchronization triggered successfully in background.", "success")
                fetchIntegrationStatus()
            } else {
                Notifications(res?.message || "Failed to sync clients.", "error")
            }
        } catch (e) {
            Notifications("Error syncing clients.", "error")
        } finally {
            setClientSyncLoading(false)
        }
    }

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2 text-muted small">Loading Scanning & Tracking status...</p>
            </div>
        )
    }

    const labCode = integrationStatus?.payload?.lab_code || "dglab"
    const integratedAt = integrationStatus?.integrated_at

    return (
        <div>
            {/* HERO BANNER & 1-CLICK LAUNCH */}
            <Card
                className="border-0 shadow-sm mb-4"
                style={{
                    borderRadius: "14px",
                    background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
                    color: "#fff",
                    cursor: isIntegrated ? "pointer" : "default",
                    position: "relative",
                    overflow: "hidden"
                }}
            >
                <CardBody className="p-4">
                    <Row className="align-items-center g-3">
                        <Col lg={7} onClick={isIntegrated ? handleLaunchPortal : undefined}>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <span
                                    className="badge px-3 py-1"
                                    style={{
                                        background: "rgba(56, 189, 248, 0.15)",
                                        color: "#38bdf8",
                                        border: "1px solid rgba(56, 189, 248, 0.25)",
                                        borderRadius: "20px",
                                        fontWeight: "700",
                                        fontSize: "11px"
                                    }}
                                >
                                    <i className="bx bx-check-shield me-1" /> Super Admin & Admin Managed
                                </span>
                                {isIntegrated && (
                                    <span
                                        className="badge px-2 py-1"
                                        style={{
                                            background: "rgba(34, 197, 94, 0.2)",
                                            color: "#4ade80",
                                            borderRadius: "20px",
                                            fontWeight: "700",
                                            fontSize: "11px"
                                        }}
                                    >
                                        ● Connected to scan.oralstop.com
                                    </span>
                                )}
                            </div>
                            <h4 className="fw-bold text-white mb-2">
                                Oralstop Scanning & Tracking System
                            </h4>
                            <p className="text-white-50 mb-0" style={{ fontSize: "13px", lineHeight: "1.6" }}>
                                Connect your lab with OralStop digital intraoral scanners, real-time case tracking, technician field workflows, and doctor clinic appointment bookings.
                            </p>
                        </Col>

                        <Col lg={5} className="text-lg-end d-flex flex-column flex-sm-row justify-content-lg-end gap-2">
                            <button
                                className="btn d-inline-flex align-items-center justify-content-center gap-2 px-4 py-2"
                                onClick={handleLaunchPortal}
                                disabled={launching || !isIntegrated}
                                style={{
                                    background: "linear-gradient(135deg, #c2410c 0%, #ea580c 100%)",
                                    color: "#ffffff",
                                    border: "none",
                                    borderRadius: "10px",
                                    fontWeight: "700",
                                    fontSize: "13px",
                                    boxShadow: "0 4px 14px rgba(234, 88, 12, 0.45)",
                                    transition: "all 0.2s"
                                }}
                            >
                                {launching ? <Spinner size="sm" /> : <i className="bx bx-link-external" style={{ fontSize: "16px" }} />}
                                Launch Scanning Portal
                            </button>

                            <button
                                className="btn btn-outline-light d-inline-flex align-items-center justify-content-center gap-2 px-3 py-2"
                                onClick={() => setIsBookingsModalOpen(true)}
                                disabled={!isIntegrated}
                                style={{ borderRadius: "10px", fontWeight: "600", fontSize: "13px" }}
                            >
                                <i className="bx bx-calendar-event" style={{ fontSize: "16px" }} />
                                Clinic Bookings
                            </button>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {notice && (
                <Alert color={notice.type} toggle={() => setNotice(null)} className="mb-4" style={{ borderRadius: "10px" }}>
                    {notice.text}
                </Alert>
            )}

            {/* MASTER INTEGRATION CARD */}
            <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "14px" }}>
                <CardBody className="p-4">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                        <div className="d-flex align-items-center gap-3">
                            <div
                                style={{
                                    width: "48px",
                                    height: "48px",
                                    borderRadius: "12px",
                                    background: isIntegrated ? "rgba(2, 132, 199, 0.1)" : "#f1f5f9",
                                    color: isIntegrated ? "#0284c7" : "#94a3b8",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "24px"
                                }}
                            >
                                <i className="bx bx-radar" />
                            </div>
                            <div>
                                <div className="d-flex align-items-center gap-2">
                                    <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: "15px" }}>
                                        Scanning & Tracking Integration
                                    </h6>
                                    <Badge color={isIntegrated ? "success" : "secondary"} pill className="px-2 py-1">
                                        {isIntegrated ? "Active" : "Disabled"}
                                    </Badge>
                                </div>
                                <div className="text-muted small mt-1">
                                    Enables cloud intraoral scanner connections, token authentication, and auto-synchronization.
                                </div>
                            </div>
                        </div>

                        <div className="d-flex align-items-center gap-3">
                            <div className="text-end d-none d-md-block">
                                <div className="fw-bold text-dark small">Lab Identifier</div>
                                <code className="text-primary fw-bold">{labCode}</code>
                            </div>
                            <div className="form-check form-switch form-switch-lg mb-0">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    checked={isIntegrated}
                                    onChange={(e) => handleMasterToggle(e.target.checked)}
                                    disabled={actionLoading}
                                    style={{ cursor: "pointer", width: "48px", height: "24px" }}
                                />
                            </div>
                        </div>
                    </div>
                </CardBody>
            </Card>

            {/* SYNC CONFIGURATION & SUB-MODULES (Displayed only when Master Toggle is ON) */}
            {isIntegrated ? (
                <Row className="g-4 mb-4">
                    {/* Synchronization Modules */}
                    <Col lg={7}>
                        <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "14px" }}>
                            <CardBody className="p-4">
                                <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
                                    <h6 className="fw-bold mb-0 text-dark">
                                        <i className="bx bx-sync text-primary me-2" /> Sync Configuration & Sub-Modules
                                    </h6>
                                    <span className="badge bg-light text-success border px-2 py-1" style={{ fontSize: "11px" }}>
                                        <i className="bx bx-shield-check me-1" /> Token Stored & Active
                                    </span>
                                </div>
                                <p className="text-muted small mb-4">
                                    When enabled, the system automatically synchronizes existing and new data with the OralStop Scanning cloud.
                                </p>

                                <div className="d-flex flex-column gap-3">
                                    {/* 1. AGENT SYNC */}
                                    <div className="p-3 rounded border" style={{ background: "#f8fafc" }}>
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <div className="d-flex align-items-center gap-2">
                                                <i className="bx bx-user-pin text-primary" style={{ fontSize: "20px" }} />
                                                <div>
                                                    <div className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                                                        1. Agent Sync
                                                    </div>
                                                    <div className="text-muted small">
                                                        Synchronize lab technicians, CAD-CAM designers, and delivery agents
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="form-check form-switch form-switch-md mb-0">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={agentSyncActive}
                                                    onChange={(e) => {
                                                        setAgentSyncActive(e.target.checked)
                                                        if (e.target.checked) handleSyncAgents()
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center pt-2 border-top mt-2">
                                            <span className="text-muted small">
                                                {integratedAt
                                                    ? "Integrated: " + moment(integratedAt).format("DD MMM YYYY, hh:mm A")
                                                    : "Auto-sync ready"}
                                            </span>
                                            <button
                                                className="btn btn-sm btn-link text-primary p-0 fw-semibold"
                                                onClick={handleSyncAgents}
                                                disabled={agentSyncLoading || !agentSyncActive}
                                            >
                                                {agentSyncLoading ? <Spinner size="sm" /> : <><i className="bx bx-refresh me-1" /> Sync Now</>}
                                            </button>
                                        </div>
                                    </div>

                                    {/* 2. DOCTOR / CLIENT SYNC */}
                                    <div className="p-3 rounded border" style={{ background: "#f8fafc" }}>
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <div className="d-flex align-items-center gap-2">
                                                <i className="bx bx-buildings text-primary" style={{ fontSize: "20px" }} />
                                                <div>
                                                    <div className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                                                        2. Doctor / Client Sync
                                                    </div>
                                                    <div className="text-muted small">
                                                        Synchronize dental clinics, practicing doctors, addresses, and contacts
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="form-check form-switch form-switch-md mb-0">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={clientSyncActive}
                                                    onChange={(e) => {
                                                        setClientSyncActive(e.target.checked)
                                                        if (e.target.checked) handleSyncClients()
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center pt-2 border-top mt-2">
                                            <span className="text-muted small">
                                                {integratedAt
                                                    ? "Integrated: " + moment(integratedAt).format("DD MMM YYYY, hh:mm A")
                                                    : "Auto-sync ready"}
                                            </span>
                                            <button
                                                className="btn btn-sm btn-link text-primary p-0 fw-semibold"
                                                onClick={handleSyncClients}
                                                disabled={clientSyncLoading || !clientSyncActive}
                                            >
                                                {clientSyncLoading ? <Spinner size="sm" /> : <><i className="bx bx-refresh me-1" /> Sync Now</>}
                                            </button>
                                        </div>
                                    </div>

                                    {/* 3. TRACKING MODULE */}
                                    <div className="p-3 rounded border" style={{ background: "#f8fafc" }}>
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <div className="d-flex align-items-center gap-2">
                                                <i className="bx bx-map-pin text-primary" style={{ fontSize: "20px" }} />
                                                <div>
                                                    <div className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                                                        3. Tracking Module
                                                    </div>
                                                    <div className="text-muted small">
                                                        Enable logistics, case checkpoint tracking, and pickup/delivery stages
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="form-check form-switch form-switch-md mb-0">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={trackingModuleActive}
                                                    onChange={(e) => handleToggleTracking(e.target.checked)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>

                    {/* Auto-Login & Details Side Card */}
                    <Col lg={5}>
                        <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "14px" }}>
                            <CardBody className="p-4">
                                <h6 className="fw-bold mb-3 pb-2 border-bottom text-dark">
                                    <i className="bx bx-key text-primary me-2" /> Panel Access & Auto-Login
                                </h6>
                                <p className="text-muted small mb-3">
                                    Your lab is registered with OralStop Cloud API. The authentication token is securely stored for automatic single-sign-on.
                                </p>

                                <div className="p-3 rounded border mb-3" style={{ background: "#f8fafc" }}>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="text-muted small fw-semibold">Lab Identifier:</span>
                                        <code className="text-primary fw-bold">{labCode}</code>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="text-muted small fw-semibold">Status:</span>
                                        <Badge color="success" pill>Registered & Active</Badge>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span className="text-muted small fw-semibold">Auto-Login Token:</span>
                                        <span className="badge bg-light text-dark border">AES Token Stored</span>
                                    </div>
                                </div>

                                <div className="d-grid gap-2">
                                    <button
                                        className="btn btn-primary d-flex align-items-center justify-content-center gap-2 py-2"
                                        onClick={handleLaunchPortal}
                                        disabled={launching}
                                        style={{ borderRadius: "8px", fontWeight: "700" }}
                                    >
                                        {launching ? <Spinner size="sm" /> : <i className="bx bx-link-external" />}
                                        Open Scanner Portal (Auto-Login)
                                    </button>

                                    <button
                                        className="btn btn-outline-secondary d-flex align-items-center justify-content-center gap-2 py-2"
                                        onClick={() => setIsBookingsModalOpen(true)}
                                        style={{ borderRadius: "8px", fontWeight: "600" }}
                                    >
                                        <i className="bx bx-calendar-event" />
                                        View Doctor Scan Bookings
                                    </button>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            ) : (
                /* Disabled State Callout */
                <Card className="border-0 shadow-sm" style={{ borderRadius: "14px", background: "#f8fafc" }}>
                    <CardBody className="p-4 text-center py-5">
                        <div
                            className="avatar-md mx-auto mb-3 rounded-circle d-flex align-items-center justify-content-center"
                            style={{ background: "#e2e8f0", color: "#64748b" }}
                        >
                            <i className="bx bx-power-off" style={{ fontSize: "28px" }} />
                        </div>
                        <h5 className="fw-bold text-dark mb-1">Scanning & Tracking Integration is OFF</h5>
                        <p className="text-muted small mb-3" style={{ maxWidth: "480px", margin: "0 auto" }}>
                            Turn on the master switch above to automatically verify your Lab ID with Oralstop, retrieve an authentication token, and configure auto-sync for Doctors, Agents, and Tracking.
                        </p>
                        <button
                            className="btn btn-primary px-4"
                            onClick={() => handleMasterToggle(true)}
                            disabled={actionLoading}
                            style={{ borderRadius: "8px", fontWeight: "700" }}
                        >
                            {actionLoading ? <Spinner size="sm" /> : "Enable Scanning & Tracking"}
                        </button>
                    </CardBody>
                </Card>
            )}

            {/* Bookings Modal */}
            <ScanningBookingsModal
                isOpen={isBookingsModalOpen}
                toggle={() => setIsBookingsModalOpen(prev => !prev)}
            />
        </div>
    )
}

export default ScanningIntegration
